import { StatusLabelEnum } from 'src/app/components/status-label/status-label.component';
import { AuthenticationMethod, Environment, Platform } from '.';
import { MenuAction } from '../menuAction.model';
import { InternalNavigationTriggerModel } from '../navigation/internalNavigationTrigger.model';
import { ICardField } from '../viewModels/cardModel';

export enum StatusCodeEnum {
  on = 'on',
  off = 'off',
}

export function getIntegrationStatusLabel(integrationStatus: IntegrationStatusModel): StatusLabelEnum | undefined {
  if (integrationStatus.code === StatusCodeEnum.on) {
    return StatusLabelEnum.active;
  }
  if (integrationStatus.code === StatusCodeEnum.off && integrationStatus.reason === StatusReasonEnum.failing) {
    return StatusLabelEnum.failed;
  }
  if (integrationStatus.code === StatusCodeEnum.off && integrationStatus.reason === StatusReasonEnum.deactivated) {
    return StatusLabelEnum.inactive;
  }

  return undefined;
}

export enum StatusReasonEnum {
  failing = 'fail',
  deactivated = 'deactivated',
  deleted = 'deleted',
  none = 'none',
}

export interface IntegrationStatusModel {
  readonly code: StatusCodeEnum;
  readonly reason: StatusReasonEnum;
  readonly errorDisplayMessage?: string;
}

export enum IntegrationActionsEnum {
  enable = 'enable',
  disable = 'disable',
  edit = 'edit',
  delete = 'delete',
  retry = 'retry',
}

export const IntegrationsActionsNamesToLabelsMap: {
  [key in IntegrationActionsEnum]: string;
} = {
  [IntegrationActionsEnum.enable]: 'integrationsPage.integrationActions.enableAction',
  [IntegrationActionsEnum.disable]: 'integrationsPage.integrationActions.disableAction',
  [IntegrationActionsEnum.edit]: 'integrationsPage.integrationActions.editAction',
  [IntegrationActionsEnum.delete]: 'integrationsPage.integrationActions.deleteAction',
  [IntegrationActionsEnum.retry]: 'integrationsPage.integrationActions.retryAction',
};

export type IntegrationCredentials = { [key: string]: string };

export class IntegrationModel {
  public readonly id: string;
  public readonly zenityAccountId: string;
  public readonly name: string;
  public readonly description: string;
  public readonly environment: Environment;
  public readonly platform: Platform;
  public readonly authenticationMethod: AuthenticationMethod;
  public readonly connectionStatus: IntegrationStatusModel;
  public readonly createdBy: string;
  public readonly lastUpdatedBy: string;
  public readonly createdAt: Date;
  public readonly lastUpdated: Date;
}

interface IntegrationScan {
  integrationId: string;
  scan: {
    endTime: string | null | undefined;
  };
  dataCollection: {
    startTime: string | null | undefined;
  };
}

export interface IntegrationsScansResponse {
  integrationsScans: IntegrationScan[];
}

export type CombinedIntegration = IntegrationModel & {
  lastScanEndedAt: string | null | undefined;
  dataCollectionStartedAt: string | null | undefined;
};

/**
 * Sorts integrations by status (active > failing > inactive) and then by last updated time.
 * Use this for the Integrations page where status-based ordering is preferred.
 */
export const sortIntegrationsByStatus = (integrations: CombinedIntegration[]): CombinedIntegration[] =>
  [...integrations].sort((integration1, integration2) => {
    const hasSameStatus =
      integration1.connectionStatus.code === integration2.connectionStatus.code &&
      integration1.connectionStatus.reason === integration2.connectionStatus.reason;

    if (hasSameStatus) {
      return integration1.lastUpdated > integration2.lastUpdated ? -1 : 1;
    }

    if (integration1.connectionStatus.code === StatusCodeEnum.on) {
      return -1;
    }
    if (integration2.connectionStatus.code === StatusCodeEnum.on) {
      return 1;
    }
    if (integration1.connectionStatus.reason === StatusReasonEnum.failing) {
      return -1;
    }
    return 1;
  });

export interface IntegrationToAddModel {
  readonly name: string;
  readonly description: string;
  readonly platform: Platform;
  readonly authenticationMethod: AuthenticationMethod;
  readonly environment: string;
  readonly credentials: IntegrationCredentials;
}

export interface EditIntegrationInputModel {
  readonly name?: string;
  readonly description?: string;
  readonly environment?: string;
}

export interface IntegrationCreationResponseModel {
  readonly zenityAccountId: string;
  readonly integrationId: string;
}

export interface IntegrationCredentialUpdateModel {
  readonly credentials_without_secrets: { [key: string]: string };
  readonly secret_credentials: { [key: string]: string };
}

export interface IntegrationAuthenticationUpdateModel {
  readonly authenticationMethod: AuthenticationMethod;
  readonly credentials: IntegrationCredentials;
}

export interface IInfoCard {
  cardId: string;
  primaryCardTitle: string;
  secondaryCardTitle: string;
  secondaryCardValue: string;
  secondaryCardTooltipText: string;
  statusLabel?: StatusLabelEnum;
  statusTooltip?: string;
  menuActions?: MenuAction[];
  leftCardFields: ICardField[];
  rightCardFields: ICardField[];
  internalNavigation: InternalNavigationTriggerModel;
  hasInlineWebhook?: boolean;
  platformLogoPath?: string;
  cardType?: 'integration' | 'adapter';
}

export interface CardActionClicked {
  card: IInfoCard;
  action: MenuAction;
}
